<template>
  <div class="contact-collection">
    <component
      :is="contactNameCollectionComponent"
      v-model="mutable"
      :show-validation="showValidation"
      :contact-title-dropdown="contactTitleDropdown"
      :custom-titles="customTitles"
      @validate="validateSubComponent"></component>

    <component
      :is="jobFunctionCollectionComponent"
      v-model="mutable"
      :job-functions="jobFunctions"
      :show-validation="showValidation"
      @validate="validateSubComponent"></component>

    <component
      :is="emailAddressCollectionComponent"
      v-model="mutable"
      :show-validation="showValidation"
      @validate="validateSubComponent"></component>

    <component
      :is="telephoneCollectionComponent"
      v-model="mutable"
      :show-validation="showValidation"
      @validate="validateSubComponent"></component>

    <component
      :is="organisationCollectionComponent"
      v-model="mutable"
      :school-search-enable-manual-entry="schoolSearchEnableManualEntry"
      :address-search-enable-manual-entry="addressSearchEnableManualEntry"
      :international-search-enable-manual-entry="internationalSearchEnableManualEntry"
      :fields="organisationCollectionFields"
      :show-validation="showValidation"
      :search-type-id="searchTypeId"
      :org-id="orgId"
      :countries="countries"
      :label="searchLabel"
      :help-text="searchHelpText"
      :allow-non-search-registration="allowNonSearchRegistration"
      :non-search-organisation-id="nonSearchOrganisationId"
      :non-search-label="nonSearchLabel"
      @validate="validateSubComponent"></component>
  </div>
</template>

<script>
import { cloneDeep, isEqual } from "lodash";
import vmodelMixin from "~/mixins/vmodelMixin";
import AllFieldsNameCollectionMethod from "#/LandingPages/ContactCollection/AllFieldsNameCollectionMethod.vue";
import SingleFieldNameCollectionMethod from "#/LandingPages/ContactCollection/SingleFieldNameCollectionMethod.vue";
import FreeTextJobCollectionMethod from "#/LandingPages/ContactCollection/FreeTextJobCollectionMethod.vue";
import DropdownJobCollectionMethod from "#/LandingPages/ContactCollection/DropdownJobCollectionMethod.vue";
import SpecifiedJobCollectionMethod from "#/LandingPages/ContactCollection/SpecifiedJobCollectionMethod.vue";
import FreeTextEmailCollectionMethod from "#/LandingPages/ContactCollection/FreeTextEmailCollectionMethod.vue";
import TelephoneCollectionMethod from "#/LandingPages/ContactCollection/TelephoneCollectionMethod.vue";
import FreeTextOrganisationCollectionMethod from "#/LandingPages/ContactCollection/FreeTextOrganisationCollectionMethod.vue";
import InternationalSearchOrganisationCollectionMethod from "#/LandingPages/ContactCollection/InternationalSearchOrganisationCollectionMethod.vue";
import SchoolSearchOrganisationCollectionMethod from "#/LandingPages/ContactCollection/SchoolSearchOrganisationCollectionMethod.vue";
import AddressSearchOrganisationCollectionMethod from "#/LandingPages/ContactCollection/AddressSearchOrganisationCollectionMethod.vue";
import SpecifiedOrganisationCollectionMethod from "#/LandingPages/ContactCollection/SpecifiedOrganisationCollectionMethod.vue";
import * as JobFunctionCollectionMethodConstants from "~/constants/jobFunctionCollectionMethodConstants";
import * as NameCollectionMethodConstants from "~/constants/nameCollectionMethodConstants";
import * as EmailAddressCollectionMethodConstants from "~/constants/emailAddressCollectionMethodConstants";
import * as OrganisationCollectionMethodConstants from "~/constants/organisationCollectionMethodConstants";
import * as TelephoneCollectionMethodConstants from "~/constants/telephoneCollectionMethodConstants";

export default {
  name: "ContactCollection",
  components: {
    AllFieldsNameCollectionMethod,
    SingleFieldNameCollectionMethod,
    FreeTextJobCollectionMethod,
    DropdownJobCollectionMethod,
    SpecifiedJobCollectionMethod,
    FreeTextEmailCollectionMethod,
    FreeTextOrganisationCollectionMethod,
    SchoolSearchOrganisationCollectionMethod,
    InternationalSearchOrganisationCollectionMethod,
    AddressSearchOrganisationCollectionMethod,
    SpecifiedOrganisationCollectionMethod,
    TelephoneCollectionMethod
  },
  mixins: [vmodelMixin],
  props: {
    value: {
      type: Object,
      required: true
    },
    showValidation: {
      type: Boolean,
      default: false
    },
    contactNameCollectionMethod: {
      type: String,
      required: true,
      validator ( val ) {
        return (
          [
            NameCollectionMethodConstants.ALL_FIELDS,
            NameCollectionMethodConstants.SINGLE_NAME_FIELD
          ].indexOf( val ) !== -1
        );
      }
    },
    contactTitleDropdown: {
      type: Boolean,
      default: false
    },
    jobFunctionCollectionMethod: {
      type: String,
      required: true,
      validator ( val ) {
        return (
          [
            JobFunctionCollectionMethodConstants.FREE_TEXT,
            JobFunctionCollectionMethodConstants.DROPDOWN,
            JobFunctionCollectionMethodConstants.SPECIFIED
          ].indexOf( val ) !== -1
        );
      }
    },
    specifiedJob: {
      type: String,
      default: null
    },
    jobFunctionId: {
      type: String,
      default: null
    },
    customTitles: {
      type: Array,
      default: null
    },
    emailAddressCollectionMethod: {
      type: String,
      default: null,
      validator ( val ) {
        return [null, EmailAddressCollectionMethodConstants.FREE_TEXT].indexOf( val ) !== -1;
      }
    },
    telephoneCollectionMethod: {
      type: String,
      default: null,
      validator ( val ) {
        return [null, TelephoneCollectionMethodConstants.TEL].indexOf( val ) !== -1;
      }
    },
    organisationCollectionMethod: {
      type: String,
      required: true,
      validator ( val ) {
        return (
          [
            OrganisationCollectionMethodConstants.FREE_TEXT,
            OrganisationCollectionMethodConstants.SCHOOL_SEARCH,
            OrganisationCollectionMethodConstants.ADDRESS_SEARCH,
            OrganisationCollectionMethodConstants.INTERNATIONAL_SEARCH,
            OrganisationCollectionMethodConstants.SPECIFIED
          ].indexOf( val ) !== -1
        );
      }
    },
    organisationCollectionFields: {
      type: Object,
      default: () => ( {
        schoolName: false,
        postcode: false
      } )
    },
    schoolSearchEnableManualEntry: {
      type: Boolean,
      default: false
    },
    addressSearchEnableManualEntry: {
      type: Boolean,
      default: false
    },
    internationalSearchEnableManualEntry: {
      type: Boolean,
      default: false
    },
    jobFunctions: {
      type: Array,
      default: () => []
    },
    countries: {
      type: Array,
      default: () => []
    },
    customSchoolSearchTypeId: {
      type: Number,
      default: -1
    },
    schoolSearchTypeId: {
      type: Number,
      default: 1
    },
    internationalSearchTypeId: {
      type: Number,
      default: 1
    },
    orgId: {
      type: String,
      default: null
    },
    createWebAccount: {
      type: Boolean,
      default: false
    },
    useJobFunction: {
      type: Boolean,
      default: false
    },
    webAccountTypeId: {
      type: String,
      default: null
    },
    webUserTypeId: {
      type: String,
      default: null
    },
    searchLabel: {
      type: String,
      default: undefined
    },
    searchHelpText: {
      type: String,
      default: undefined
    },
    allowNonSearchRegistration: {
      type: Boolean,
      default: false
    },
    nonSearchOrganisationId: {
      type: String,
      default: null
    },
    nonSearchLabel: {
      type: String,
      default: null
    }
  },
  data: () => ( {
    mutable: null,
    subComponents: []
  } ),
  computed: {
    searchTypeId () {
      let result = 0;

      const hasCustomValue = this.customSchoolSearchTypeId && this.customSchoolSearchTypeId > -1;

      // eslint-disable-next-line default-case
      switch ( this.organisationCollectionMethod ) {
        case OrganisationCollectionMethodConstants.INTERNATIONAL_SEARCH: {
          result = hasCustomValue ? this.customSchoolSearchTypeId : this.internationalSearchTypeId;
          break;
        }
        case OrganisationCollectionMethodConstants.SCHOOL_SEARCH: {
          result = hasCustomValue > -1 ? this.customSchoolSearchTypeId : this.schoolSearchTypeId;
          break;
        }
      }

      return result;
    },
    contactNameCollectionComponent () {
      let result = null;

      // eslint-disable-next-line default-case
      switch ( this.contactNameCollectionMethod ) {
        case NameCollectionMethodConstants.ALL_FIELDS: {
          result = "AllFieldsNameCollectionMethod";
          break;
        }
        case NameCollectionMethodConstants.SINGLE_NAME_FIELD: {
          result = "SingleFieldNameCollectionMethod";
          break;
        }
      }

      return result;
    },
    jobFunctionCollectionComponent () {
      switch ( this.jobFunctionCollectionMethod ) {
        case JobFunctionCollectionMethodConstants.SPECIFIED: {
          return "SpecifiedJobCollectionMethod";
        }
        case JobFunctionCollectionMethodConstants.FREE_TEXT: {
          return "FreeTextJobCollectionMethod";
        }
        case JobFunctionCollectionMethodConstants.DROPDOWN: {
          return "DropdownJobCollectionMethod";
        }
        default: {
          return null;
        }
      }
    },
    emailAddressCollectionComponent () {
      let result = null;

      // eslint-disable-next-line default-case
      switch ( this.emailAddressCollectionMethod ) {
        case EmailAddressCollectionMethodConstants.FREE_TEXT: {
          result = "FreeTextEmailCollectionMethod";
          break;
        }
      }

      return result;
    },
    telephoneCollectionComponent () {
      let result = null;

      // eslint-disable-next-line default-case
      switch ( this.telephoneCollectionMethod ) {
        case TelephoneCollectionMethodConstants.TEL: {
          result = "TelephoneCollectionMethod";
          break;
        }
      }

      return result;
    },
    organisationCollectionComponent () {
      let result = null;

      // eslint-disable-next-line default-case
      switch ( this.organisationCollectionMethod ) {
        case OrganisationCollectionMethodConstants.FREE_TEXT: {
          result = "FreeTextOrganisationCollectionMethod";
          break;
        }
        case OrganisationCollectionMethodConstants.SCHOOL_SEARCH: {
          result = "SchoolSearchOrganisationCollectionMethod";
          break;
        }
        case OrganisationCollectionMethodConstants.ADDRESS_SEARCH: {
          result = "AddressSearchOrganisationCollectionMethod";
          break;
        }
        case OrganisationCollectionMethodConstants.INTERNATIONAL_SEARCH: {
          result = "InternationalSearchOrganisationCollectionMethod";
          break;
        }
        case OrganisationCollectionMethodConstants.SPECIFIED: {
          result = "SpecifiedOrganisationCollectionMethod";
          break;
        }
      }

      return result;
    },
    isValid () {
      let result = true;

      if ( this.subComponents && this.subComponents.length > 0 ) {
        result = this.subComponents.every( x => x.valid );
      }

      return result;
    }
  },
  watch: {
    createWebAccount () {
      this.updateMutableWithProps();
    },
    useJobFunction () {
      this.updateMutableWithProps();
    },
    webAccountTypeId () {
      this.updateMutableWithProps();
    },
    webUserTypeId () {
      this.updateMutableWithProps();
    },
    isValid () {
      this.validate();
    },
    mutable: {
      deep: true,
      handler () {
        this.updateMutableWithProps();
      }
    }
  },
  methods: {
    updateMutableWithProps () {
      const clone = cloneDeep( this.mutable );

      if ( clone ) {
        clone.createWebAccount = this.createWebAccount;
        clone.webAccountTypeId = this.webAccountTypeId;
        clone.webUserTypeId = this.webUserTypeId;
        clone.useJobFunction = this.useJobFunction;
      }

      if ( this.jobFunctionCollectionMethod === JobFunctionCollectionMethodConstants.SPECIFIED ) {
        clone.jobTitle = this.specifiedJob;
        clone.jobFunctionId = this.jobFunctionId;
      }

      if ( !isEqual( this.mutable, clone ) ) {
        this.mutable = clone;
      }
    },
    validateSubComponent ( val ) {
      if ( val ) {
        const match = this.subComponents.find( x => x.alias === val.alias );

        if ( !match ) {
          this.subComponents.push( val );
        } else {
          const index = this.subComponents.indexOf( match );
          this.subComponents[index].valid = val.valid;
        }
      }
    },
    validate () {
      this.$emit( "validate", {
        alias: this.$options.name,
        valid: this.isValid
      } );
    }
  }
};
</script>

<style lang="less" scoped></style>
